import React, { useEffect, useState } from 'react';
import { X, Instagram, Music, Facebook, Twitter, Youtube, Linkedin } from 'lucide-react';
import { Button } from '../ui';
import { platformService, Platform } from '../../services/platformService';
import { Customer } from '../../types/customer';
import { PlatformAccount } from '../../types/platformAccount';
import PlatformAccountForm from './PlatformAccountForm';
import { useToast } from '../../contexts/ToastContext';
import { Organization } from '../../services/organizationService';


interface OrganizationPlatformAccountDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  platformAccount?: any | null;
  isCreating: boolean;
  customers: Customer[];
  organization: Organization;
  onSaved: () => void;
  activeAccountCount?: number;
  maxSocialAccounts?: number;
}

const OrganizationPlatformAccountDrawer: React.FC<OrganizationPlatformAccountDrawerProps> = ({
  isOpen,
  onClose,
  platformAccount,
  isCreating,
  customers,
  organization,
  onSaved,
  activeAccountCount,
  maxSocialAccounts
}) => {
  const { showError } = useToast();
  const [platforms, setPlatforms] = useState<Platform[]>([]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, isOpen]);

  // Prevent background scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  // Fetch platforms for dynamic icon rendering
  useEffect(() => {
    if (!isOpen) return;

    const fetchPlatforms = async () => {
      try {
        const response = await platformService.getPlatforms();
        setPlatforms(response.data.platforms);
      } catch (err) {
        showError('Failed to load platforms', err instanceof Error ? err.message : 'An unexpected error occurred');
      }
    };

    fetchPlatforms();
  }, [isOpen, showError]);

  const getPlatformIcon = (platformName: string) => {
    const name = platformName.toLowerCase();
    switch (name) {
      case 'instagram': return Instagram;
      case 'tiktok': return Music;
      case 'facebook': return Facebook;
      case 'twitter': case 'x': return Twitter;
      case 'youtube': return Youtube;
      case 'linkedin': return Linkedin;
      default: return Instagram;
    }
  };

  const PlatformIcon = platformAccount?.platform ? getPlatformIcon(platformAccount.platform) : Instagram;

  // Convert platform account for the form
  const convertedAccount: PlatformAccount | null = platformAccount ? {
    id: platformAccount.id,
    customerId: platformAccount.customerId,
    platformId: platforms.find(p => p.platformName?.toLowerCase() === platformAccount.platform?.toLowerCase())?.id || 1,
    username: platformAccount.username,
    displayName: platformAccount.displayName || platformAccount.username,
    profileUrl: platformAccount.profileUrl || '',
    followerCount: platformAccount.followers || 0,
    followingCount: platformAccount.following || 0,
    totalVideos: platformAccount.videos || 0,
    verified: platformAccount.isVerified || false,
    emvViewRate: parseFloat(platformAccount.viewRate?.toString() || '0'),
    emvLikeRate: parseFloat(platformAccount.likeRate?.toString() || '0'),
    emvCommentRate: parseFloat(platformAccount.commentRate?.toString() || '0'),
    emvShareRate: parseFloat(platformAccount.shareRate?.toString() || '0'),
    isActive: platformAccount.isActive !== undefined ? platformAccount.isActive : true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    platform: {
      id: platforms.find(p => p.platformName?.toLowerCase() === platformAccount.platform?.toLowerCase())?.id || 1,
      name: platformAccount.platform || 'Instagram',
      displayName: platformAccount.platform || 'Instagram'
    }
  } : null;

  const handleSuccess = () => {
    onSaved();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-white shadow-xl transform transition-transform translate-x-0">
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center space-x-3">
              <PlatformIcon className="h-5 w-5 text-gray-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {isCreating ? 'Add Social Media Account' : 'Edit Social Media Account'}
                </h2>
                <p className="text-sm text-gray-600">
                  {isCreating 
                    ? <>Add social media account to <span className="font-semibold">{organization.organizationName}</span></> 
                    : `Update @${platformAccount?.username}`
                  }
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="p-2"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <PlatformAccountForm
              customer={customers[0] || null} // Use the organization's customer
              account={convertedAccount}
              onCancel={onClose}
              onSuccess={handleSuccess}
              isDrawer={true}
              activeAccountCount={activeAccountCount}
              maxSocialAccounts={maxSocialAccounts}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationPlatformAccountDrawer;